import styles from './LayoutAccordion.module.css'
import { LayoutStack } from '../LayoutStack'
import {
  type ReactNode,
  type SyntheticEvent,
  type TransitionEventHandler,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import classNames from 'classnames'
import { Icon } from '../../Icon'
import { getScrollParent } from '../../Popup'
import type { Spacing } from '../../types'

type AccordionState = {
  activeKey?: string
  prevKey?: string | undefined
  scrollOnOpen?: boolean
  onTransitionFinished: (next: string) => void
}

const AccordionContext = createContext<AccordionState>({
  activeKey: undefined,
  prevKey: undefined,
  onTransitionFinished: (_) => {},
  scrollOnOpen: true,
})

export function useAccordionContext() {
  return useContext(AccordionContext)
}
export function LayoutAccordionItem({
  label,
  id: key,
  className,
  children,
  onClick,
}: {
  id: string
  label: ReactNode
  className?: string
  children?: ReactNode
  onClick: (key: string) => void
}) {
  const { activeKey, onTransitionFinished, prevKey, scrollOnOpen } = useAccordionContext()
  const [open, setOpen] = useState(activeKey === key || prevKey === key)
  const contentRef = useRef<HTMLDivElement>(null)
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const onSummaryClicked = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault()
      onClick(key)
    },
    [onClick, key]
  )
  const handleTransitionEnd: TransitionEventHandler = useCallback(
    (ev) => {
      if (ev.propertyName !== 'opacity') return
      if (ev.target !== contentRef.current) return
      if (activeKey !== key) return
      onTransitionFinished(key)
      if (!scrollOnOpen) return
      window.requestAnimationFrame(() => {
        if (!detailsRef.current) return
        const scrollContainer = getScrollParent(detailsRef.current)
        if (!scrollContainer) return
        const { top } = detailsRef.current.getBoundingClientRect()
        const offsetTop = scrollContainer === window ? 0 : (scrollContainer as HTMLElement).offsetTop
        scrollContainer.scrollBy({ behavior: 'smooth', top: top - offsetTop })
      })
    },
    [activeKey, key, onTransitionFinished, scrollOnOpen]
  )

  useEffect(() => {
    if (activeKey === prevKey) return
    const timeout = setTimeout(() => setOpen(activeKey === key), 1)
    return () => clearTimeout(timeout)
  }, [key, activeKey, prevKey])

  // TODO: Find a way to make this work with axe-core, we need to be able to handle nested links here
  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <details
      open
      id={key}
      aria-expanded={activeKey === key ? 'true' : 'false'}
      className={classNames(styles.accordionItem, { [styles.open!]: open }, className)}
      ref={detailsRef}
    >
      <summary onClick={onSummaryClicked} className={styles.accordionSummary}>
        {label}
        <Icon name="chevronDown" className={styles.accordionIcon} size="400" />
      </summary>
      <div className={styles.wrapper} onTransitionEnd={handleTransitionEnd} ref={contentRef}>
        <div className={styles.content} inert={activeKey !== key}>
          {children}
        </div>
      </div>
    </details>
  )
}

export type LayoutAccordionProps = {
  activeKey?: string
  onTransitionFinished?: (next: string, previous: string | undefined) => void
  defaultKey: string
  children?: ReactNode
  className?: string
  space?: Spacing
  animationSpeedInMs?: number
  scrollOnOpen?: boolean
}

export default function LayoutAccordion({
  defaultKey,
  children,
  activeKey: externalActiveKey,
  onTransitionFinished,
  className,
  space = '300',
  animationSpeedInMs = 500,
  scrollOnOpen = true,
}: LayoutAccordionProps) {
  const [prevKey, setPrevKey] = useState<string | undefined>(undefined)
  const [activeKey, setActiveKey] = useState(externalActiveKey ?? defaultKey)
  const onFinish = useCallback(
    (next: string) => {
      onTransitionFinished?.(next, prevKey)
      setPrevKey(next)
    },
    [onTransitionFinished, prevKey]
  )

  useEffect(() => {
    if (typeof externalActiveKey !== 'string') return
    setActiveKey(externalActiveKey)
    setPrevKey(activeKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalActiveKey])

  return (
    <LayoutStack
      className={classNames(styles.accordion, className)}
      space={space}
      style={
        {
          '--animation-speed': `${animationSpeedInMs}ms`,
          '--space': `var(--betterplace-spacing-${space})`,
        } as React.CSSProperties
      }
    >
      <AccordionContext.Provider value={{ activeKey, onTransitionFinished: onFinish, prevKey, scrollOnOpen }}>
        {children}
      </AccordionContext.Provider>
    </LayoutStack>
  )
}
